import { type FormEvent, type ReactNode, useRef, useState } from 'react';
import { api, type Library } from '../api.ts';
import {
  AlertIcon,
  CloseIcon,
  FilmIcon,
  FlameIcon,
  FolderIcon,
  MusicIcon,
  SnowIcon,
  TvIcon,
} from '../Icon.tsx';
import { useModalFocus } from '../useModalFocus.ts';

export interface AddLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (newLib: Library, scanStarted: boolean) => void;
  initialKind?: 'movie' | 'show' | 'mixed' | 'music' | undefined;
  initialName?: string | undefined;
  initialPath?: string | undefined;
}

export function AddLibraryModal({
  isOpen,
  onClose,
  onCreated,
  initialKind = 'mixed',
  initialName = '',
  initialPath = '',
}: AddLibraryModalProps): ReactNode {
  const [kind, setKind] = useState<'movie' | 'show' | 'mixed' | 'music'>(initialKind);
  const isMusicKind = (value: 'movie' | 'show' | 'mixed' | 'music'): boolean =>
    value === 'music' || value === 'mixed';
  const [name, setName] = useState(
    initialName ||
      (initialKind === 'show' ? 'Diziler' : isMusicKind(initialKind) ? 'Müzikler' : 'Filmler'),
  );
  const [path, setPath] = useState(
    initialPath ||
      (initialKind === 'show'
        ? '/srv/media/diziler'
        : isMusicKind(initialKind)
          ? '/srv/media/muzikler'
          : '/srv/media/films'),
  );
  const [layer, setLayer] = useState<'hot' | 'cold'>('hot');
  /**
   * Yan dosya altyazı klasörü (ADR-016).
   *
   * Boş bırakılırsa yalnız filmin kendi klasörü aranır.
   */
  const [subtitlePath, setSubtitlePath] = useState('');
  const [autoScan, setAutoScan] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useModalFocus(dialogRef, onClose, nameRef, isOpen);

  if (!isOpen) return null;

  const handleKindChange = (newKind: 'movie' | 'show' | 'mixed' | 'music') => {
    setKind(newKind);
    if (!initialName) {
      setName(newKind === 'show' ? 'Diziler' : isMusicKind(newKind) ? 'Müzikler' : 'Filmler');
    }
    if (!initialPath) {
      setPath(
        newKind === 'show'
          ? '/srv/media/diziler'
          : isMusicKind(newKind)
            ? '/srv/media/muzikler'
            : '/srv/media/films',
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !path.trim()) {
      setError('Lütfen kütüphane adı ve sunucu klasör yolunu doldurun.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const created = await api.createLibrary({
        name: name.trim(),
        kind,
        path: path.trim(),
        layer,
        // Boş bırakılırsa sunucu "yalnız medyanın yanı" davranışına düşer.
        ...(subtitlePath.trim() ? { subtitlePath: subtitlePath.trim() } : {}),
      });

      let scanStarted = false;
      if (autoScan) {
        try {
          await api.scanLibrary(created.id, true);
          scanStarted = true;
        } catch {
          // Tarama başlatılamasa bile kütüphane oluşturuldu
        }
      }

      onCreated(created, scanStarted);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Kütüphane oluşturulurken bir hata oluştu. Lütfen dosya yolu ve izinleri kontrol edin.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-lib-modal__backdrop">
      <button
        type="button"
        className="add-lib-modal__backdrop-btn"
        onClick={onClose}
        aria-label="Kapat"
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        className="add-lib-modal__card"
        role="dialog"
        aria-labelledby="add-lib-title"
        aria-modal="true"
        tabIndex={-1}
      >
        <div className="add-lib-modal__header">
          <div className="add-lib-modal__header-icon" aria-hidden="true">
            <FolderIcon size={20} />
          </div>
          <div className="add-lib-modal__header-text">
            <h2 id="add-lib-title">Yeni Medya Kütüphanesi Ekle</h2>
            <p>Sunucunuzdaki film, dizi veya müzik klasörünü bağlayarak koleksiyonunuza ekleyin.</p>
          </div>
          <button
            type="button"
            className="add-lib-modal__close-btn"
            onClick={onClose}
            aria-label="Kapat"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {error && (
          <div className="add-lib-modal__error" role="alert">
            <AlertIcon size={15} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="add-lib-modal__form">
          {/* Tür Seçimi */}
          <div className="add-lib-modal__field">
            <div className="add-lib-modal__label">Kütüphane Türü</div>
            <div className="add-lib-modal__kind-grid">
              <button
                type="button"
                className={`add-lib-modal__kind-btn ${kind === 'movie' ? 'active' : ''}`}
                onClick={() => handleKindChange('movie')}
              >
                <span className="add-lib-modal__kind-icon">
                  <FilmIcon size={22} />
                </span>
                <span className="add-lib-modal__kind-title">Film</span>
                <span className="add-lib-modal__kind-desc">Sinema filmleri ve belgeseller</span>
              </button>

              <button
                type="button"
                className={`add-lib-modal__kind-btn ${kind === 'show' ? 'active' : ''}`}
                onClick={() => handleKindChange('show')}
              >
                <span className="add-lib-modal__kind-icon">
                  <TvIcon size={22} />
                </span>
                <span className="add-lib-modal__kind-title">Dizi</span>
                <span className="add-lib-modal__kind-desc">Sezon ve bölüm hiyerarşisi</span>
              </button>

              <button
                type="button"
                className={`add-lib-modal__kind-btn ${kind === 'music' ? 'active' : ''}`}
                onClick={() => handleKindChange('music')}
              >
                <span className="add-lib-modal__kind-icon">
                  <MusicIcon size={22} />
                </span>
                <span className="add-lib-modal__kind-title">Müzik</span>
                <span className="add-lib-modal__kind-desc">Albümler, sanatçılar ve şarkılar</span>
              </button>

              <button
                type="button"
                className={`add-lib-modal__kind-btn ${kind === 'mixed' ? 'active' : ''}`}
                onClick={() => handleKindChange('mixed')}
              >
                <span className="add-lib-modal__kind-icon">
                  <FolderIcon size={22} />
                </span>
                <span className="add-lib-modal__kind-title">Karışık</span>
                <span className="add-lib-modal__kind-desc">Film, dizi ve müzik bir arada</span>
              </button>
            </div>
          </div>

          {/* Adı */}
          <div className="add-lib-modal__field">
            <label htmlFor="lib-name" className="add-lib-modal__label">
              Kütüphane Adı
            </label>
            <input
              id="lib-name"
              type="text"
              ref={nameRef}
              className="add-lib-modal__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Müzikler, Yabancı Diziler, 4K Filmler"
              required
            />
          </div>

          {/* Klasör Yolu */}
          <div className="add-lib-modal__field">
            <label htmlFor="lib-path" className="add-lib-modal__label">
              Sunucu Klasör Yolu (Absolute Path)
            </label>
            <input
              id="lib-path"
              type="text"
              className="add-lib-modal__input add-lib-modal__input--code"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/srv/media/muzikler veya /srv/media/diziler"
              required
            />
            <p className="add-lib-modal__hint">
              Sunucunuzdaki gerçek dosya sistemi yolunu girin. Örneğin Linux için{' '}
              <code>/srv/media/muzikler</code> veya <code>/srv/media/diziler</code>.
            </p>
          </div>

          {/* Altyazı Klasörü (ADR-016) */}
          <div className="add-lib-modal__field">
            <label htmlFor="lib-subtitle-path" className="add-lib-modal__label">
              Altyazı Klasörü (İsteğe Bağlı)
            </label>
            <input
              id="lib-subtitle-path"
              type="text"
              className="add-lib-modal__input add-lib-modal__input--code"
              value={subtitlePath}
              onChange={(e) => setSubtitlePath(e.target.value)}
              placeholder="Boş bırakılırsa yalnız filmin yanındaki altyazılar aranır"
            />
            <p className="add-lib-modal__hint">
              Altyazıları ayrı bir klasörde tutuyorsanız burayı yazın. Dosya adı filmle aynı olmalı
              (<code>Film.srt</code>, <code>Film.tr.srt</code>).
            </p>
          </div>

          {/* Katman Seçimi (ADR-010) */}
          <div className="add-lib-modal__field">
            <div className="add-lib-modal__label">Depolama Katmanı</div>
            <div className="add-lib-modal__layer-select">
              <label className={`add-lib-modal__radio-card ${layer === 'hot' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="layer"
                  value="hot"
                  checked={layer === 'hot'}
                  onChange={() => setLayer('hot')}
                />
                <div className="add-lib-modal__radio-content">
                  <strong>
                    <FlameIcon size={14} />
                    Sıcak Katman (Hot)
                  </strong>
                  <span>VPS yerel diski / SSD (yüksek hızlı doğrudan erişim)</span>
                </div>
              </label>

              <label className={`add-lib-modal__radio-card ${layer === 'cold' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="layer"
                  value="cold"
                  checked={layer === 'cold'}
                  onChange={() => setLayer('cold')}
                />
                <div className="add-lib-modal__radio-content">
                  <strong>
                    <SnowIcon size={14} />
                    Soğuk Katman (Cold)
                  </strong>
                  <span>Ev arşivi / WireGuard tüneli (SMB bağlantısı)</span>
                </div>
              </label>
            </div>
          </div>

          {/* Otomatik Tarama Checkbox */}
          <div className="add-lib-modal__checkbox-field">
            <label className="add-lib-modal__checkbox-label">
              <input
                type="checkbox"
                checked={autoScan}
                onChange={(e) => setAutoScan(e.target.checked)}
              />
              <span>Kütüphane oluşturulduktan sonra medyaları hemen tara</span>
            </label>
          </div>

          {/* Butonlar */}
          <div className="add-lib-modal__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Vazgeç
            </button>
            <button type="submit" className="button button--primary" disabled={isSubmitting}>
              {isSubmitting ? 'Oluşturuluyor...' : 'Kütüphaneyi Bağla ve Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
